import { colors, Typography } from "@material-ui/core";
import React, { useState } from "react";
import { useSelector } from "react-redux";

import { ConfigureDestinationsModal } from "../../modals/ConfigureDestinationsModal";
import {
  getDefaultValuesFromConfigFields,
  getNonSensitiveSettings,
} from "../../modals/ConfigureIntegrationModal";
import { DestinationConfigRow } from "../shared/ConfigPageRow";

const CredentialsModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig,
}) => {
  const settings = existingConfig.settings || {};

  const defaultValueMap = getDefaultValuesFromConfigFields(
    destination.configFields,
  );
  const nonSensitiveSettings = getNonSensitiveSettings(
    settings,
    destination.configFields,
  );
  const [values, setValues] = useState({
    ...defaultValueMap,
    ...nonSensitiveSettings,
  });

  let limitSettings = ["access_key", "domain", "events_enabled"];
  if (values.events_enabled) {
    limitSettings = limitSettings.concat([
      "events_shared_secret_key",
      "tenant_id",
      "event_topic_name",
      "contract_id",
    ]);
  }

  return (
    <ConfigureDestinationsModal
      limitSettings={limitSettings}
      open={open}
      includeHeader={true}
      settings={settings}
      dest={destination}
      onClose={onClose}
      onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
      existingConfig={existingConfig}
      valuesStateObjFromParent={[values, setValues]}
    />
  );
};

const CredentialsRow = ({ setConfigModalOpen, existingConfig }) => {
  const settings = existingConfig.settings || {};
  let isConfigured = "access_key" in settings && !!settings.domain;
  if (settings.events_enabled) {
    if (
      !(
        "events_shared_secret_key" in settings &&
        settings.tenant_id &&
        settings.event_topic_name
      )
    ) {
      isConfigured = false;
    }
  }

  return (
    <DestinationConfigRow
      name="Credentials"
      textColor={isConfigured ? "" : colors.red[600]}
      labelText={isConfigured ? "Configured" : "Not configured"}
      onOpenConfigModal={() => setConfigModalOpen("credentials")}
    />
  );
};

const PersonMappingsModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig,
}) => {
  const settings = existingConfig.settings || {};
  const defaultValueMap = getDefaultValuesFromConfigFields(
    destination.configFields,
  );
  const nonSensitiveSettings = getNonSensitiveSettings(
    settings,
    destination.configFields,
  );
  const [values, setValues] = useState({
    ...defaultValueMap,
    ...nonSensitiveSettings,
  });

  let limitSettings = ["send_user_id"];
  if (values.send_user_id) {
    limitSettings.push("user_id_field_name");
  }
  limitSettings.push("person_field_mapping");

  const header = (
    <p className="text-textSubtle text-xs">
      When you call <code className="inline text-xs">freshpaint.identify</code>,
      we create a Person object on Gainsight. You can see then by visiting{" "}
      <span style={{ color: "blue" }}>
        {"Administration > Customer Data > Data Management"}
      </span>
      , then searching for <i>person</i> in{" "}
      <i>{"Search by an object's Name"}</i>, and finally selecting the object
      with name <span style={{ color: "blue" }}>Person</span>. (Actual list of
      tracked users is visible under the <i>Data</i> tab).
      <br />
      <br />
      Gainsight does not support sending arbitary user props. {"You'll"} need to
      allowlist them first. Some props like Email, Name etc. are allowlisted by
      default. You can allowlist your custom props on Gainsight.
      <br />
      <br />
      In this configuration you can map props passed to{" "}
      <code className="inline text-xs">freshpaint.identify</code> to fields on
      Gainsight. {"You'll"} need the corresponding <b>Field Name</b>, which you
      can find by clicking on the field on Gainsight.
    </p>
  );

  return (
    <ConfigureDestinationsModal
      overrideHeader={header}
      limitSettings={limitSettings}
      open={open}
      settings={settings}
      dest={destination}
      onClose={onClose}
      onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
      existingConfig={existingConfig}
      valuesStateObjFromParent={[values, setValues]}
    />
  );
};

const PersonMappingsRow = ({ setConfigModalOpen }) => {
  return (
    <DestinationConfigRow
      name="User mappings"
      labelText="Mappings"
      onOpenConfigModal={setConfigModalOpen}
    />
  );
};

const EventMappingsModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig,
}) => {
  const settings = existingConfig.settings || {};
  const defaultValueMap = getDefaultValuesFromConfigFields(
    destination.configFields,
  );
  const nonSensitiveSettings = getNonSensitiveSettings(
    settings,
    destination.configFields,
  );
  const [values, setValues] = useState({
    ...defaultValueMap,
    ...nonSensitiveSettings,
  });

  let limitSettings = [
    "event_name_mapping",
    "event_version_mapping",
    "send_user_id_as_event_prop",
  ];
  if (values.send_user_id_as_event_prop) {
    limitSettings.push("user_id_event_prop_name");
  }

  const header = (
    <p className="text-textSubtle text-xs">
      Before sending events to Gainsight {"you'll"} need to create them first.{" "}
      <br />
      <br />
      You can do that by visiting {"Gainsight's Events Page ("}{" "}
      <span style={{ color: "blue" }}>
        {"Administration > General > Events"}
      </span>
      {") "}, select the <span style={{ color: "blue" }}>Events</span> tab and
      then click on <span style={{ color: "blue" }}>Create Event</span> button.
      <br />
      <br />
      For any event that you create on Gainsight {"you'll"} need to specify a
      name and version. Then use this to specify mapping from Freshpaint Event
      Name to Gainsight Event Name and Version.
    </p>
  );

  return (
    <ConfigureDestinationsModal
      overrideHeader={header}
      limitSettings={limitSettings}
      open={open}
      settings={settings}
      dest={destination}
      onClose={onClose}
      onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
      existingConfig={existingConfig}
      valuesStateObjFromParent={[values, setValues]}
    />
  );
};

const EventMappingsRow = ({ setConfigModalOpen }) => {
  return (
    <DestinationConfigRow
      name="Event mappings"
      labelText="Mappings"
      onOpenConfigModal={setConfigModalOpen}
    />
  );
};

const CREDENTIALS = "CREDENTIALS";
const PERSON_MAPPINGS = "PERSON_MAPPINGS";
const EVENT_MAPPINGS = "EVENT_MAPPINGS";

const Rows = {
  CREDENTIALS: CredentialsRow,
  PERSON_MAPPINGS: PersonMappingsRow,
  EVENT_MAPPINGS: EventMappingsRow,
};

const Modals = {
  CREDENTIALS: CredentialsModal,
  PERSON_MAPPINGS: PersonMappingsModal,
  EVENT_MAPPINGS: EventMappingsModal,
};

const RowOrder = [CREDENTIALS, PERSON_MAPPINGS, EVENT_MAPPINGS];

export default {
  Rows,
  RowOrder,
  Modals,
};
